import { enemyAppearance } from '../animation/Theme';
import { DirectionalSpriteController } from '../animation/DirectionalSpriteController';
import Phaser from 'phaser';
import { TILE, MAP_COLS, MAP_ROWS } from '../constants';
import { EnemyType, CellType, type Grid } from '../types';

// Speed in pixels per second for each enemy type
const ENEMY_SPEEDS: Record<EnemyType, number> = {
  [EnemyType.BASIC]:   TILE * 1.0,
  [EnemyType.FAST]:    TILE * 2.0,
  [EnemyType.HUNTER]:  TILE * 1.2,
  [EnemyType.SMART]:   TILE * 1.3,
  [EnemyType.BOMBER]:  TILE * 1.0,
  [EnemyType.BOSS]:    TILE * 1.5,
};

const ENEMY_HP: Record<EnemyType, number> = {
  [EnemyType.BASIC]:   1,
  [EnemyType.FAST]:    1,
  [EnemyType.HUNTER]:  1,
  [EnemyType.SMART]:   1,
  [EnemyType.BOMBER]:  1,
  [EnemyType.BOSS]:    5,
};

// Bomb timer range for BOMBER type (milliseconds)
const BOMBER_TIMER_MIN = 3000;
const BOMBER_TIMER_MAX = 5000;

type Direction = 'up' | 'down' | 'left' | 'right';

const DIR_VECTORS: Record<Direction, { dc: number; dr: number }> = {
  up:    { dc:  0, dr: -1 },
  down:  { dc:  0, dr:  1 },
  left:  { dc: -1, dr:  0 },
  right: { dc:  1, dr:  0 },
};

const ALL_DIRS: Direction[] = ['up', 'down', 'left', 'right'];

// Shuffle array in place using Fisher-Yates
function shuffle<T>(arr: T[]): T[] {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

export class Enemy extends Phaser.GameObjects.Container {
  // Type & stats
  type: EnemyType;
  hp: number;
  maxHp: number;
  alive: boolean;

  // Grid position
  col: number;
  row: number;
  targetCol: number;
  targetRow: number;

  // Smooth visual position (pixels, grid origin = 0,0)
  pixelX: number;
  pixelY: number;

  // Movement
  speed: number;        // pixels per second
  moving: boolean;
  direction: Direction;
  private bombBlocksCell: (col: number, row: number) => boolean = () => false;

  // AI state
  dangerMap: Set<string>;
  playerCol: number;
  playerRow: number;

  // BOMBER-specific
  bombTimer: number;

  // BOSS-specific
  private bossPhase: number;
  private bossMoveTimer: number;
  private bossPatternDir: Direction;

  // Graphics references for animation
  private healthBar: Phaser.GameObjects.Graphics | null = null;
  private sprite: Phaser.GameObjects.Sprite;
  private visual: DirectionalSpriteController;

  constructor(scene: Phaser.Scene, col: number, row: number, type: EnemyType) {
    // Container position is the center of the tile
    super(scene, col * TILE + TILE / 2, row * TILE + TILE / 2);

    this.type = type;
    this.maxHp = ENEMY_HP[type];
    this.hp = this.maxHp;
    this.alive = true;

    this.col = col;
    this.row = row;
    this.targetCol = col;
    this.targetRow = row;

    this.pixelX = col * TILE + TILE / 2;
    this.pixelY = row * TILE + TILE / 2;

    this.speed = ENEMY_SPEEDS[type];
    this.moving = false;
    this.direction = shuffle([...ALL_DIRS])[0];

    this.dangerMap = new Set<string>();
    this.playerCol = 0;
    this.playerRow = 0;

    this.bombTimer = BOMBER_TIMER_MIN + Math.random() * (BOMBER_TIMER_MAX - BOMBER_TIMER_MIN);

    this.bossPhase = 0;
    this.bossMoveTimer = 0;
    this.bossPatternDir = shuffle([...ALL_DIRS])[0];

    // Build visuals
    this.add(scene.add.ellipse(0, 19, type === EnemyType.BOSS ? 44 : 32, 10, 0x000000, 0.22));
    const appearance = enemyAppearance(type);
    this.sprite = scene.add.sprite(0, 0, appearance.texture, appearance.frame);
    this.add(this.sprite);
    this.visual = new DirectionalSpriteController(this.sprite, appearance.character, type === EnemyType.BOSS ? 0.48 : 0.43);
    scene.add.existing(this);
  }

  // ---------------------------------------------------------------------------
  // Visual construction
  // ---------------------------------------------------------------------------

  private drawHealthBar(): void {
    if (this.healthBar) {
      this.healthBar.destroy();
    }
    const barW = TILE - 8;
    const barH = 5;
    const barY = -(TILE / 2) - 10;

    const hbGfx = this.scene.add.graphics();
    hbGfx.fillStyle(0x333333, 1);
    hbGfx.fillRect(-barW / 2, barY, barW, barH);

    const ratio = Math.max(0, this.hp / this.maxHp);
    const fillColor = ratio > 0.5 ? 0x44ff44 : ratio > 0.25 ? 0xffaa00 : 0xff2222;
    hbGfx.fillStyle(fillColor, 1);
    hbGfx.fillRect(-barW / 2, barY, barW * ratio, barH);

    hbGfx.lineStyle(1, 0x000000, 0.8);
    hbGfx.strokeRect(-barW / 2, barY, barW, barH);

    this.add(hbGfx);
    this.healthBar = hbGfx;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  setBombCollisionQuery(query: (col: number, row: number) => boolean): void {
    this.bombBlocksCell = query;
  }

  update(delta: number, grid: Grid): void {
    if (!this.alive) return;

    const dt = delta / 1000; // seconds

    // Update BOMBER timer
    if (this.type === EnemyType.BOMBER) {
      this.bombTimer -= delta;
    }

    if (this.moving && (this.targetCol !== this.col || this.targetRow !== this.row) &&
        this.bombBlocksCell(this.targetCol, this.targetRow)) {
      // A bomb can appear after a step has begun. Retreat smoothly to the last cell.
      this.direction = this.vecToDir(this.col - this.targetCol, this.row - this.targetRow);
      this.bossPatternDir = this.direction;
      this.targetCol = this.col;
      this.targetRow = this.row;
    }

    if (this.moving) {
      // Interpolate toward target
      const targetPixelX = this.targetCol * TILE + TILE / 2;
      const targetPixelY = this.targetRow * TILE + TILE / 2;

      const dx = targetPixelX - this.pixelX;
      const dy = targetPixelY - this.pixelY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const step = this.speed * dt;

      if (dist <= step) {
        // Arrived at target cell
        this.pixelX = targetPixelX;
        this.pixelY = targetPixelY;
        this.col = this.targetCol;
        this.row = this.targetRow;
        this.moving = false;
        this.setPosition(this.pixelX, this.pixelY);
        this.chooseNextMove(grid);
      } else {
        this.pixelX += (dx / dist) * step;
        this.pixelY += (dy / dist) * step;
        this.setPosition(this.pixelX, this.pixelY);
      }
    } else {
      this.chooseNextMove(grid);
    }

    this.updateDirectionalAnim(delta);

    // BOSS phase changes based on HP ratio
    if (this.type === EnemyType.BOSS) {
      const hpRatio = this.hp / this.maxHp;
      if (hpRatio <= 0.25) {
        this.bossPhase = 3;
      } else if (hpRatio <= 0.5) {
        this.bossPhase = 2;
      } else if (hpRatio <= 0.75) {
        this.bossPhase = 1;
      } else {
        this.bossPhase = 0;
      }
    }
  }

  takeDamage(amount: number = 1): boolean {
    if (!this.alive) return true;
    this.hp -= amount;

    // Flash red
    this.scene.tweens.add({
      targets: this,
      alpha: 0.3,
      duration: 80,
      yoyo: true,
      repeat: 2,
    });

    // Redraw HP bar for boss
    if (this.type === EnemyType.BOSS) {
      this.drawHealthBar();
    }

    if (this.hp <= 0) {
      this.die();
      return true;
    }
    return false;
  }

  getGridPos(): { col: number; row: number } {
    return { col: this.col, row: this.row };
  }

  updateDangerMap(cells: Set<string>): void {
    this.dangerMap = cells;
  }

  updatePlayerPos(col: number, row: number): void {
    this.playerCol = col;
    this.playerRow = row;
  }

  canPlaceBomb(): boolean {
    return this.type === EnemyType.BOMBER && this.bombTimer <= 0;
  }

  resetBombTimer(): void {
    this.bombTimer = BOMBER_TIMER_MIN + Math.random() * (BOMBER_TIMER_MAX - BOMBER_TIMER_MIN);
  }

  private updateDirectionalAnim(delta: number): void {
    if (this.moving && (this.targetCol !== this.col || this.targetRow !== this.row))
      this.direction = this.vecToDir(this.targetCol - this.col, this.targetRow - this.row);
    this.visual.update(delta, this.moving, this.direction, this.dangerMap.has(`${this.col},${this.row}`));
  }

  die(): void {
    if (!this.alive) return;
    this.alive = false;
    this.visual.action('death', 1000);
    this.visual.update(0, false);

    // Death animation: scale up, fade out
    this.scene.tweens.add({
      targets: this,
      scaleX: 1.8,
      scaleY: 1.8,
      alpha: 0,
      duration: 350,
      ease: 'Power2',
      onComplete: () => {
        this.destroy();
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Movement helpers
  // ---------------------------------------------------------------------------

  private chooseNextMove(grid: Grid): void {
    const forward = DIR_VECTORS[this.direction];
    if (this.bombBlocksCell(this.col + forward.dc, this.row + forward.dr)) {
      const dc = -forward.dc, dr = -forward.dr;
      this.direction = this.vecToDir(dc, dr);
      this.bossPatternDir = this.direction;
      if (this.isPassable(this.col + dc, this.row + dr, grid)) {
        this.targetCol = this.col + dc;
        this.targetRow = this.row + dr;
        this.moving = true;
        return;
      }
    }
    let dc = 0;
    let dr = 0;

    switch (this.type) {
      case EnemyType.BASIC:
      case EnemyType.BOMBER:
        ({ dc, dr } = this.aiBasic(grid));
        break;
      case EnemyType.FAST:
        ({ dc, dr } = this.aiFast(grid));
        break;
      case EnemyType.HUNTER:
        ({ dc, dr } = this.aiHunter(grid));
        break;
      case EnemyType.SMART:
        ({ dc, dr } = this.aiSmart(grid));
        break;
      case EnemyType.BOSS:
        ({ dc, dr } = this.aiBoss(grid));
        break;
    }

    if (dc !== 0 || dr !== 0) {
      this.targetCol = this.col + dc;
      this.targetRow = this.row + dr;
      this.moving = true;
    }
  }

  // BASIC: try current direction first, then pick random valid
  private aiBasic(grid: Grid): { dc: number; dr: number } {
    const valid = this.getValidDirections(grid);
    if (valid.length === 0) return { dc: 0, dr: 0 };

    const cur = DIR_VECTORS[this.direction];
    const canContinue = valid.some(d => d.dc === cur.dc && d.dr === cur.dr);

    if (canContinue && Math.random() > 0.2) {
      return cur;
    }

    // Pick random
    const chosen = valid[Math.floor(Math.random() * valid.length)];
    this.direction = this.vecToDir(chosen.dc, chosen.dr);
    return chosen;
  }

  // FAST: pick random valid direction each time
  private aiFast(grid: Grid): { dc: number; dr: number } {
    const valid = this.getValidDirections(grid);
    if (valid.length === 0) return { dc: 0, dr: 0 };
    const chosen = valid[Math.floor(Math.random() * valid.length)];
    this.direction = this.vecToDir(chosen.dc, chosen.dr);
    return chosen;
  }

  // HUNTER: BFS one step toward player
  private aiHunter(grid: Grid): { dc: number; dr: number } {
    const step = this.bfsStep(grid, this.col, this.row, this.playerCol, this.playerRow);
    if (step) {
      this.direction = this.vecToDir(step.dc, step.dr);
      return step;
    }
    // Fallback to basic
    return this.aiBasic(grid);
  }

  // SMART: avoid danger zones; pick safe valid direction
  private aiSmart(grid: Grid): { dc: number; dr: number } {
    const allValid = this.getValidDirections(grid);
    if (allValid.length === 0) return { dc: 0, dr: 0 };

    // Filter out cells in dangerMap
    const safe = allValid.filter(d => {
      const nc = this.col + d.dc;
      const nr = this.row + d.dr;
      return !this.dangerMap.has(`${nc},${nr}`);
    });

    const candidates = safe.length > 0 ? safe : allValid;
    const chosen = candidates[Math.floor(Math.random() * candidates.length)];
    this.direction = this.vecToDir(chosen.dc, chosen.dr);
    return chosen;
  }

  // BOSS: chase player but periodically do pattern movement
  private aiBoss(grid: Grid): { dc: number; dr: number } {
    this.bossMoveTimer++;

    // Phase affects chase aggressiveness
    const patternInterval = Math.max(2, 8 - this.bossPhase * 2);

    if (this.bossMoveTimer % patternInterval === 0) {
      // Chase player via BFS
      const step = this.bfsStep(grid, this.col, this.row, this.playerCol, this.playerRow);
      if (step) {
        this.direction = this.vecToDir(step.dc, step.dr);
        return step;
      }
    }

    // Pattern movement: keep going in bossPatternDir until blocked
    const patVec = DIR_VECTORS[this.bossPatternDir];
    const nc = this.col + patVec.dc;
    const nr = this.row + patVec.dr;

    if (this.isPassable(nc, nr, grid)) {
      return patVec;
    }

    // Change pattern direction
    const valid = this.getValidDirections(grid);
    if (valid.length === 0) return { dc: 0, dr: 0 };
    const chosen = valid[Math.floor(Math.random() * valid.length)];
    this.bossPatternDir = this.vecToDir(chosen.dc, chosen.dr);
    this.direction = this.bossPatternDir;
    return chosen;
  }

  // BFS returning first step direction toward target; null if unreachable
  private bfsStep(
    grid: Grid,
    startC: number,
    startR: number,
    goalC: number,
    goalR: number
  ): { dc: number; dr: number } | null {
    if (startC === goalC && startR === goalR) return null;

    type Node = { c: number; r: number; firstDir: { dc: number; dr: number } | null };
    const visited = new Set<string>();
    const queue: Node[] = [{ c: startC, r: startR, firstDir: null }];
    visited.add(`${startC},${startR}`);

    while (queue.length > 0) {
      const node = queue.shift()!;

      for (const dir of ALL_DIRS) {
        const vec = DIR_VECTORS[dir];
        const nc = node.c + vec.dc;
        const nr = node.r + vec.dr;
        const key = `${nc},${nr}`;

        if (visited.has(key)) continue;
        if (!this.isPassable(nc, nr, grid)) continue;

        const firstDir = node.firstDir ?? vec;

        if (nc === goalC && nr === goalR) {
          return firstDir;
        }

        visited.add(key);
        queue.push({ c: nc, r: nr, firstDir });
      }
    }
    return null;
  }

  getValidDirections(grid: Grid): Array<{ dc: number; dr: number }> {
    const result: Array<{ dc: number; dr: number }> = [];
    for (const dir of ALL_DIRS) {
      const { dc, dr } = DIR_VECTORS[dir];
      const nc = this.col + dc;
      const nr = this.row + dr;
      if (this.isPassable(nc, nr, grid)) {
        result.push({ dc, dr });
      }
    }
    return result;
  }

  private isPassable(c: number, r: number, grid: Grid): boolean {
    if (c < 0 || c >= MAP_COLS || r < 0 || r >= MAP_ROWS) return false;
    if (this.bombBlocksCell(c, r)) return false;
    const cell = grid[r][c];
    return cell !== CellType.WALL && cell !== CellType.BLOCK;
  }

  private vecToDir(dc: number, dr: number): Direction {
    if (dc === 1)  return 'right';
    if (dc === -1) return 'left';
    if (dr === 1)  return 'down';
    return 'up';
  }
}
